import { motion } from 'framer-motion';
import { BarChart3, Target, Rocket, TrendingUp, Award, Users, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const stats = [
    {
      value: '500+',
      label: 'Brands Managed',
      description: 'Across marketplaces'
    },
    {
      value: 'Multi-Platform',
      label: 'Marketplace Expertise',
      description: 'Amazon, Flipkart & more'
    },
    {
      value: 'Proven',
      label: 'Revenue Growth Framework',
      description: 'Data-driven approach'
    }
  ];

  const approach = [
    {
      icon: Target,
      title: 'Strategy',
      description: 'Data-driven marketplace analysis and growth planning.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Rocket,
      title: 'Execution',
      description: 'Optimized listings, advertising and account management.',
      color: 'from-primary to-blue-500'
    },
    {
      icon: TrendingUp,
      title: 'Scaling',
      description: 'Continuous performance monitoring and revenue optimization.',
      color: 'from-secondary to-orange-500'
    }
  ];

  const expertise = [
    'Amazon & Flipkart marketplace management',
    'PPC campaign optimization & management',
    'Product listing & SEO optimization',
    'A+ content & brand store creation',
    'Quick commerce platform management',
    'Performance-driven growth strategies'
  ];

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>About Obiquiste Solutions | E-commerce Experts in India</title>
        <meta name="description" content="Obiquiste Solutions is a leading e-commerce service provider based in Faridabad. We help brands grow on Amazon, Flipkart, Myntra, Blinkit & Swiggy Instamart." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/about" />
      </Helmet>
      {/* Banner Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 163, 224, 0.05) 0%, rgba(237, 28, 36, 0.05) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 data-testid="about-heading" className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
                Built for <span className="text-gradient">Marketplace Growth</span>
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8">
                We help ambitious brands scale profitably across Amazon, Flipkart, Myntra, Ajio and leading quick commerce platforms through structured account management and performance-driven marketing.
              </p>
              <Link to="/contact">
                <Button className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full text-base font-semibold shadow-lg">
                  Partner With Us
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ 
                  y: [0, -10, 0],
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-2xl"></div>
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/216a6a1a-ff3d-4c72-88de-4c9fd30cd839/images/0ea5b94682a282ada84bca848c00147c95036daecb1b1f8121c3ab50fc13e007.png"
                  alt="E-commerce Analytics Dashboard"
                  className="relative rounded-2xl shadow-2xl"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:shadow-lg transition-all"
              >
                <div className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-3">
                  {stat.value}
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {stat.label}
                </h3>
                <p className="text-slate-600">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Who We Are
              </h2>
              <div className="space-y-6 text-lg leading-relaxed text-slate-700">
                <p>
                  Obiquiste Solutions is a performance-focused ecommerce growth agency helping brands scale across major marketplaces. From account management to advertising optimization, we build structured growth systems designed to increase visibility, conversions and profitability.
                </p>
                <p>
                  Our team combines marketplace expertise, catalog optimization strategies and data-driven advertising frameworks to deliver measurable results across platforms.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 rounded-2xl shadow-md"
            >
              <h3 className="font-heading text-2xl font-semibold mb-6 text-center">Our Expertise</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {expertise.map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Our Approach
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A structured methodology for sustainable marketplace growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {approach.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="group relative"
              >
                <div className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border border-slate-200 hover:border-primary/50 hover:shadow-xl transition-all h-full">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex items-center mb-3">
                    <span className="text-4xl font-bold text-slate-200 mr-3">{String(idx + 1).padStart(2, '0')}</span>
                    <h3 className="font-heading text-2xl font-bold text-slate-900">{step.title}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
                {idx < approach.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-primary to-transparent"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              What Drives Us
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-md border border-slate-200"
            >
              <Award className="w-12 h-12 text-primary mb-4" />
              <h3 className="font-heading text-xl font-bold mb-3">Performance-Focused</h3>
              <p className="text-slate-600 leading-relaxed">
                Every strategy, campaign and optimization is built around measurable outcomes and ROI.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-md border border-slate-200"
            >
              <BarChart3 className="w-12 h-12 text-primary mb-4" />
              <h3 className="font-heading text-xl font-bold mb-3">Data-Driven</h3>
              <p className="text-slate-600 leading-relaxed">
                We use analytics and marketplace insights to guide every decision and optimization.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-md border border-slate-200"
            >
              <Users className="w-12 h-12 text-primary mb-4" />
              <h3 className="font-heading text-xl font-bold mb-3">Partnership Approach</h3>
              <p className="text-slate-600 leading-relaxed">
                We work as an extension of your team, invested in your long-term marketplace success.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-md border border-slate-200"
            >
              <Target className="w-12 h-12 text-primary mb-4" />
              <h3 className="font-heading text-xl font-bold mb-3">Results-Oriented</h3>
              <p className="text-slate-600 leading-relaxed">
                Our success is measured by your growth in revenue, visibility and market share.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Scale Your Marketplace Business?
            </h2>
            <p className="text-xl mb-8 text-slate-300">
              Let's discuss how we can help you achieve profitable growth across marketplaces.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full text-base font-semibold">
                  Schedule a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 rounded-full text-base font-semibold">
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
